import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProduct, getProducts } from '@/lib/api';
import { motion } from 'framer-motion';
import { fadeUp, stagger } from '@/lib/motion';
import { PageHero, Section, SectionTitle } from '@/components/shared/Section';
import { MiniForm } from '@/components/shared/Forms';
import PilotBanner from '@/components/shared/PilotBanner';
import { productSchema, breadcrumb, injectSchema } from '@/lib/schemas';
import { ShieldCheck, ArrowRight, MessageCircle, CheckCircle, ChevronRight, Tag, FileDown } from 'lucide-react';
import { usePrice } from '@/lib/priceMode';
import { isPlaceholderPrice } from '@/lib/format';

export default function ProductDetailPage() {
  const { categorySlug, productSlug } = useParams();
  const { format: formatPrice } = usePrice();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getProduct(productSlug).catch(() => null),
      getProducts(categorySlug).catch(() => ({ data: [] }))
    ]).then(([p, related]) => {
      setProduct(p?.data || null);
      setRelatedProducts((related?.data || []).filter(r => r.slug !== productSlug).slice(0, 3));
      setLoading(false);
    });
  }, [productSlug, categorySlug]);

  // Inject Product schema when product loads
  useEffect(() => {
    if (product) {
      injectSchema([
        productSchema(product),
        breadcrumb([
          { name: 'Inicio', path: '/' },
          { name: 'Hardware', path: '/hardware' },
          { name: categorySlug, path: `/hardware/${categorySlug}` },
          { name: product.name, path: `/hardware/${categorySlug}/${productSlug}` },
        ]),
      ]);
    }
  }, [product, categorySlug, productSlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#0EA5E9] border-t-transparent rounded-full animate-spin" /></div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center text-slate-500">Producto no encontrado</div>;

  return (
    <div>
      <PageHero
        badge={`${product.brand} / ${product.name}`}
        badgeIcon={ShieldCheck}
        h1={product.name}
        subtitle={product.summary}
        primaryCTA={<Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Cotizar este producto <ArrowRight className="w-4 h-4" /></Link>}
        secondaryCTA={<a href={`https://wa.me/56975294340?text=Hola,%20quiero%20cotizar%20${encodeURIComponent(product.name)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
      />

      {/* Product Details */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Image/Mockup + badges */}
          <div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-8 flex items-center justify-center aspect-square max-h-96">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
                  <span className="text-4xl font-bold text-slate-200" style={{fontFamily:'Syne,sans-serif'}}>{product.brand?.charAt(0)}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-700" style={{fontFamily:'Syne,sans-serif'}}>{product.name}</h3>
                <p className="text-sm text-[#64748B] mt-1">{product.brand}</p>
              </div>
            </div>
            {/* Model badges */}
            <div className="flex flex-wrap gap-2 mt-4">
              {product.model_type?.map((m, i) => (
                <span key={i} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${
                  m === 'venta' ? 'bg-sky-50 text-[#0EA5E9] border border-sky-100' :
                  m === 'arriendo' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                  m === 'refurb' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                  'bg-violet-50 text-violet-600 border border-violet-100'
                }`}><Tag className="w-3 h-3" />{m === 'venta' ? 'Disponible para compra' : m === 'arriendo' ? 'Disponible en arriendo' : m === 'managed' ? 'Servicio gestionado' : 'Refurbished'}</span>
              ))}
            </div>
          </div>

          {/* Right: Description + Features */}
          <div>
            <h2 className="text-xl font-semibold mb-3" style={{fontFamily:'Syne,sans-serif'}}>Descripción</h2>
            <p className="text-sm text-[#64748B] leading-relaxed mb-6">{product.description}</p>

            {/* Price + Docs */}
            {(product.price || product.price_display || product.spec_doc?.url || product.brochure_doc?.url || product.spec_pdf || product.brochure_pdf) && (
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-sky-50 to-white border border-sky-100" data-testid="product-price-docs">
                {(product.price || product.price_display) && (
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-0.5">Desde</div>
                    {isPlaceholderPrice(product.price_display || product.price) ? (
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-[#0EA5E9]" style={{fontFamily:'Syne,sans-serif'}}>Cotizar</span>
                        <span className="text-xs text-slate-400">precio sujeto a stock</span>
                      </div>
                    ) : product.liquidacion && product.price_original && !isPlaceholderPrice(product.price_original) ? (
                      <div className="flex items-baseline gap-3">
                        <span className="text-sm text-slate-400 line-through tabular-nums">{formatPrice(product.price_original)}</span>
                        <span className="text-3xl font-bold text-red-500 tabular-nums" style={{fontFamily:'Syne,sans-serif'}}>{formatPrice(product.price_display || product.price)}</span>
                        <span className="text-[10px] text-red-500 font-semibold uppercase">Liquidación</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-[#0A2540] tabular-nums" style={{fontFamily:'Syne,sans-serif'}}>{formatPrice(product.price_display || product.price)}</span>
                        <span className="text-xs text-slate-400">+ IVA</span>
                      </div>
                    )}
                  </div>
                )}
                {(product.spec_doc?.url || product.spec_pdf || product.brochure_doc?.url || product.brochure_pdf) && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-sky-100">
                    {(product.spec_doc?.url || product.spec_pdf) && (
                      <a
                        href={(product.spec_doc?.url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${product.spec_doc.url}?download=1` : (product.spec_doc?.url || product.spec_pdf))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600 transition-colors"
                        data-testid="detail-download-spec"
                      >
                        <FileDown className="w-3.5 h-3.5" />Descargar ficha técnica
                      </a>
                    )}
                    {(product.brochure_doc?.url || product.brochure_pdf) && (
                      <a
                        href={(product.brochure_doc?.url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${product.brochure_doc.url}?download=1` : (product.brochure_doc?.url || product.brochure_pdf))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-[#0A2540] border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
                        data-testid="detail-download-brochure"
                      >
                        <FileDown className="w-3.5 h-3.5" />Brochure comercial
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {product.features && product.features.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Características principales</h3>
                <ul className="space-y-2">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-[#10B981] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Specs Table */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Especificaciones técnicas</h3>
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  {Object.entries(product.specs).map(([key, value], i) => (
                    <div key={i} className={`flex ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                      <div className="w-40 shrink-0 px-4 py-2.5 text-xs font-semibold text-slate-500 border-r border-slate-100" style={{fontFamily:'JetBrains Mono, monospace'}}>{key}</div>
                      <div className="px-4 py-2.5 text-sm text-slate-700">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Pilot Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <PilotBanner />
      </div>

      {/* Quote Form */}
      <Section alt>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle badge="COTIZAR" title={`Cotiza el ${product.name}`} subtitle="Completa el formulario y un especialista te enviará una cotización personalizada en menos de 24 horas." />
          </div>
          <div className="p-6 sm:p-8 rounded-2xl bg-white border border-slate-200 shadow-lg">
            <MiniForm sourcePage={`/hardware/${categorySlug}/${productSlug}`} />
          </div>
        </div>
      </Section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <Section>
          <SectionTitle badge="PRODUCTOS RELACIONADOS" title="Otros productos en esta categoría" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedProducts.map((p) => (
              <Link key={p.slug} to={`/hardware/${categorySlug}/${p.slug}`} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow group text-center">
                <span className="text-[10px] font-semibold text-[#64748B] uppercase">{p.brand}</span>
                <h4 className="text-sm font-semibold mt-1 group-hover:text-[#0EA5E9] transition-colors">{p.name}</h4>
                <p className="text-xs text-[#64748B] mt-1">{p.summary}</p>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Breadcrumb navigation */}
      <Section>
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Link to="/hardware" className="hover:text-[#0EA5E9]">Hardware</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to={`/hardware/${categorySlug}`} className="hover:text-[#0EA5E9]">{categorySlug}</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#0A2540] font-medium">{product.name}</span>
        </div>
      </Section>
    </div>
  );
}
